import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { GoogleMapsComponent } from '../google-map/google-map.component';
import { OfflineMapComponent, CustomMapAreaData } from '../offline-map/offline-map.component';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs/internal/Subscription';
import { RequestService } from '../../services/request.service';

@Component({
  selector: 'app-smart-map',
  templateUrl: './smart-map.component.html',
  styleUrls: ['./smart-map.component.scss'],
})
export class SmartMapComponent implements OnInit {
  private subscriptions: Subscription[] = [];
  private googleMapComponent: GoogleMapsComponent | undefined;
  private offlineMapComponent: OfflineMapComponent | undefined;
  private pendingAddMarkers: { markers: any[]; options?: { calculateCenterZoom?: boolean } } | null = null;
  private pendingMarkerUpdates: Array<{
    newMarkers: any[];
    calculateCenterZoom: boolean;
    callback?: (markers: any[]) => void;
  }> = [];
  private pendingShowMarkers: { typeToHide: string[]; calculateCenterZoom: boolean } | null = null;
  private pendingCustomMapAreas: { areas: any } | null = null;
  public lnglatdefault: any = undefined;
  public zoom: number = 15;
  @Input() lazyLoad: boolean = false;
  @Input() forBuildingFlow: boolean = false;
  @Input() isCasePage: boolean = false;
  @Input() isShowDispatch: boolean = false;
  @Input() isShowAddPatrolCamera: boolean = false;
  @Input() currentGeoLocation: any = undefined;
  @Input() width: string = '100%';
  @Input() height: string = '30vh';
  @Input() enableTrackMarkers: boolean = false;
  @Input() caseId: string | undefined = undefined;
  @Input() addMyMarker: boolean = true;
  @Input() isClickable: boolean = true;
  @Input() mapDragGeoEnabled: boolean = false;
  @Input() isHoverable: boolean = false;
  @Input() isIconLabelsOn: boolean = true;
  @Input() markerIcon: string | undefined = undefined;
  @Input() setGeoLocation: any;
  @Input() setCenter: any;
  @Input() changeCenter: any;
  @Input() changeCenterAddPin: any;
  @Input() setMapTypeControl: boolean | undefined;
  @Input() setScaleControl: boolean = true;
  @Input() styles: any;
  @Input() enableAddessOnClick: boolean = false;
  @Input() goToLocationMarker: boolean = false;
  @Input() editMode: boolean = false;
  /** Passed to offline map: show Add Note / POI / car / threat in the right-click menu (default true). */
  @Input() showContextMenuNoteActions: boolean = true;
  /** Passed to offline map: show Dispatch action in right-click menu even when not in case-page mode. */
  @Input() showContextMenuDispatchAction: boolean = false;
  /** When true, Dispatch in the map context menu is disabled (e.g. threat resolved on standalone threat page). */
  @Input() contextMenuDispatchDisabled: boolean = false;
  /** Offline map: icon scale 0–100% of intrinsic size (step 10). */
  @Input() cameraIconScalePercent: number = 100;
  @Input() investigatorIconScalePercent: number = 100;
  @Input() investigatorGroupIconScalePercent: number = 100;
  @Input() noteIconScalePercent: number = 100;
  @Input() carOfInterestIconScalePercent: number = 100;
  @Input() annotationIconScalePercent: number = 100;
  @Input() personOfInterestIconScalePercent: number = 100;
  @Input() locationIconScalePercent: number = 100;
  @Input() threatIconScalePercent: number = 100;
  /** Squad / teammember (custom-placed) marker icon scale for offline map. */
  @Input() customMarkerIconScalePercent: number = 100;
  @Input() buildingIconScalePercent: number = 100;
  /** Camera FOV sector opacity in percent (0-100). */
  @Input() cameraSectorOpacityPercent: number = 100;
  /** Offline map: combine nearby markers/icons into grouped pins. */
  @Input() combineIconsOnOfflineMap: boolean = true;
  /** Offline map: render only markers near the viewport for large datasets. */
  @Input() filterMarkersToViewport: boolean = true;
  /** Camera ids selected from dashboard camera picker/list. */
  @Input() selectedCamsIds: string[] = [];
  @Output() address = new EventEmitter<string>();
  @Output() customAreaCreated = new EventEmitter<CustomMapAreaData>();
  @Output() geoLocation = new EventEmitter<any>();
  @Output() markerSelect = new EventEmitter<any>();
  @Output() markerInfoSelect = new EventEmitter<any>();
  @Output() markerDelete = new EventEmitter<any>();
  @Output() markerDragEnd = new EventEmitter<any>();
  @Output() markerRightClick = new EventEmitter<any>();
  @Output() mapSelect = new EventEmitter<any>();
  @Output() mapInitialized = new EventEmitter<any>();
  @Output() mapReady = new EventEmitter<any>();
  @Output() tilesloaded = new EventEmitter<any>();
  @Output() addressPinData = new EventEmitter<any>();
  @Output() addressJPPinData = new EventEmitter<any>();
  @Output() mapDragGeo = new EventEmitter<any>();
  @Output() shapeClick = new EventEmitter<any>();
  @Output() mapContextMenuAction = new EventEmitter<{
    action: 'centerHere' | 'copyCoordinates';
    lngLat: { lng: number; lat: number };
  }>();
  @Output() polygonUndoStateChanged = new EventEmitter<void>();

  shouldWeUseGoogle: boolean = environment.shouldWeUseGoogle;
  useGoogle: boolean = false;

  @ViewChild('googleMapRef')
  set googleMapRef(value: GoogleMapsComponent | undefined) {
    this.googleMapComponent = value;
    this.flushPendingMapOperations();
  }
  get googleMapRef(): GoogleMapsComponent | undefined {
    return this.googleMapComponent;
  }

  @ViewChild('offlineMapRef')
  set offlineMapRef(value: OfflineMapComponent | undefined) {
    this.offlineMapComponent = value;
    this.flushPendingMapOperations();
  }
  get offlineMapRef(): OfflineMapComponent | undefined {
    return this.offlineMapComponent;
  }

  constructor(private requestService: RequestService) {
  }
  private get activeMap(): any {
    return this.useGoogle ? this.googleMapRef : this.offlineMapRef;
  }

  ngOnInit(): void {
    if (this.shouldWeUseGoogle) {
      const w = window as any;
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;

      const googleAvailable =
        !!w.google &&
        !!w.google.maps &&
        w.googleMapsLoaded === true &&
        w.googleMapsFailed !== true;

      this.useGoogle = online && googleAvailable;
    }
    this.subscriptions.push(
      this.requestService.pageOrganization.subscribe((data) => {
        if (data) {
          let organization = data;
          let settings = this.requestService.getSettings(organization);
          this.lnglatdefault = settings?.defaultGeolocation || environment.lnglatdefault;
          this.zoom = settings?.defaultZoom || 15;
        }
      })
    );
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  // Proxy methods so existing code using template refs (e.g. currentMap.addMarkers)
  // continues to work transparently with the smart map wrapper.

  addMarkers(markers: any[], options?: { calculateCenterZoom?: boolean }): void {
    if (this.activeMap && typeof this.activeMap.addMarkers === 'function') {
      this.activeMap.addMarkers(markers, options);
      return;
    }
    this.pendingAddMarkers = { markers, options };
  }

  /**
   * Log markers currently managed by the underlying map implementation (offline or Google).
   * If an ID is provided, only markers matching that ID are logged.
   */
  logMarkersToConsole(id: string): any {
    if (this.activeMap) {
      return this.activeMap.logMarkersToConsole(id);
    }
    return undefined;
  }

  updateMarkers(newMarkers: any[] = [], calculateCenterZoom: boolean = true, callback: (markers: any[]) => void = undefined): void {
    if (this.activeMap && typeof this.activeMap.updateMarkers === 'function') {
      this.activeMap.updateMarkers(newMarkers, calculateCenterZoom, callback);
      return;
    }
    this.pendingMarkerUpdates.push({ newMarkers, calculateCenterZoom, callback });
  }

  setMarkerVisibilityByIds(ids: string[] = [], visible: boolean = true, callback: (markers: any[]) => void = undefined): void {
    if (this.activeMap && typeof this.activeMap.setMarkerVisibilityByIds === 'function') {
      this.activeMap.setMarkerVisibilityByIds(ids, visible, callback);
      return;
    }
    const newMarkers = ids.map((_id) => ({ _id, delete: !visible }));
    this.updateMarkers(newMarkers, false, callback);
  }

  showMarkers(typeToHide: string[] = [], calculateCenterZoom: boolean = true): void {
    if (this.activeMap && typeof this.activeMap.showMarkers === 'function') {
      this.activeMap.showMarkers(typeToHide, calculateCenterZoom);
      return;
    }
    this.pendingShowMarkers = { typeToHide, calculateCenterZoom };
  }
  showMarkersIfReady(typeToHide: string[] = [], calculateCenterZoom: boolean = true): void {
    if (this.activeMap && typeof this.activeMap.showMarkersIfReady === 'function') {
      this.activeMap.showMarkersIfReady(typeToHide, calculateCenterZoom);
      return;
    }
    this.pendingShowMarkers = { typeToHide, calculateCenterZoom };
  }

  dragToLocation(markers: any[], geolocation: { lat: any, lng: any }): void {
    if (this.activeMap && typeof this.activeMap.dragToLocation === 'function') {
      this.activeMap.dragToLocation(markers, geolocation);
    }
  }

  /**
   * Programmatically move the underlying map to a specific center and zoom.
   */
  goToCenter(geolocation: { lat: any, lng: any }, zoom: number = 15): void {
    if (!this.activeMap) {
      return;
    }
    if (typeof this.activeMap.goToCenter === 'function') {
      this.activeMap.goToCenter(geolocation, zoom);
    } else if (typeof this.activeMap.dragToLocation === 'function') {
      // Fallback for map implementations without goToCenter
      this.activeMap.dragToLocation(undefined, geolocation);
    }
  }

  getViewState(): { center: { lat: number; lng: number }; zoom: number; bearing?: number; pitch?: number } | null {
    const nativeMap = this.activeMap?.map || this.activeMap?.markersElem?.googleMap || this.activeMap?.markersElem;
    if (!nativeMap || typeof nativeMap.getCenter !== 'function' || typeof nativeMap.getZoom !== 'function') {
      return null;
    }

    const center = nativeMap.getCenter();
    if (!center) {
      return null;
    }

    return {
      center: {
        lat: typeof center.lat === 'function' ? center.lat() : center.lat,
        lng: typeof center.lng === 'function' ? center.lng() : center.lng
      },
      zoom: nativeMap.getZoom(),
      bearing: typeof nativeMap.getBearing === 'function' ? nativeMap.getBearing() : undefined,
      pitch: typeof nativeMap.getPitch === 'function' ? nativeMap.getPitch() : undefined
    };
  }

  restoreViewState(viewState: { center: { lat: number; lng: number }; zoom: number; bearing?: number; pitch?: number } | null): void {
    if (!viewState) {
      return;
    }

    const nativeMap = this.activeMap?.map || this.activeMap?.markersElem?.googleMap || this.activeMap?.markersElem;
    if (nativeMap && typeof nativeMap.jumpTo === 'function') {
      nativeMap.jumpTo({
        center: [viewState.center.lng, viewState.center.lat],
        zoom: viewState.zoom,
        bearing: viewState.bearing,
        pitch: viewState.pitch
      });
      return;
    }
    if (nativeMap && typeof nativeMap.setCenter === 'function' && typeof nativeMap.setZoom === 'function') {
      nativeMap.setCenter(viewState.center);
      nativeMap.setZoom(viewState.zoom);
      return;
    }

    this.goToCenter(viewState.center, viewState.zoom);
  }

  /** Pan without changing zoom (offline map); avoids resetting zoom on repeat building focus. */
  panToCenter(geolocation: { lat: any, lng: any }): void {
    if (!this.activeMap) {
      return;
    }
    if (typeof this.activeMap.panToCenter === 'function') {
      this.activeMap.panToCenter(geolocation);
    } else if (typeof this.activeMap.goToCenter === 'function') {
      const z =
        typeof (this.activeMap as any).map?.getZoom === 'function'
          ? (this.activeMap as any).map.getZoom()
          : 15;
      this.activeMap.goToCenter(geolocation, z);
    }
  }

  updateAndDragMarkers(newMarkers: any[] = []): void {
    if (this.activeMap && typeof this.activeMap.updateAndDragMarkers === 'function') {
      this.activeMap.updateAndDragMarkers(newMarkers);
    }
  }

  togglePath(id: string, visible: boolean = true): void {
    if (this.activeMap && typeof this.activeMap.togglePath === 'function') {
      this.activeMap.togglePath(id, visible);
    }
  }

  addPath(paths: any, id: string, color: string = '#11345a'): void {
    if (this.activeMap && typeof this.activeMap.addPath === 'function') {
      this.activeMap.addPath(paths, id, color);
    }
  }

  removePath(id: string): void {
    if (this.activeMap && typeof this.activeMap.removePath === 'function') {
      this.activeMap.removePath(id);
    }
  }

  get markersElem(): any {
    return this.activeMap ? this.activeMap.markersElem : undefined;
  }

  clearNonExistingMarkers(type: string, markers: any[]): void {
    if (this.activeMap && typeof this.activeMap.clearNonExistingMarkers === 'function') {
      this.activeMap.clearNonExistingMarkers(type, markers);
    }
  }

  clearLocationMarker(): void {
    if (this.activeMap && typeof this.activeMap.clearLocationMarker === 'function') {
      this.activeMap.clearLocationMarker();
    }
  }

  searchByText(text: string, options?: { emitMapSelect?: boolean }): void {
    if (this.activeMap && typeof this.activeMap.searchByText === 'function') {
      this.activeMap.searchByText(text, options);
    }
  }

  setEditMode(value: boolean): void {
    if (this.activeMap && typeof this.activeMap.setEditMode === 'function') {
      this.activeMap.setEditMode(value);
    }
  }

  getDrawnAreas(): Array<[number, number][]> {
    if (this.activeMap && typeof this.activeMap.getDrawnAreas === 'function') {
      return this.activeMap.getDrawnAreas();
    }
    return [];
  }

  getCustomMapAreas(): any[] {
    if (this.activeMap && typeof this.activeMap.getCustomMapAreas === 'function') {
      return this.activeMap.getCustomMapAreas();
    }
    return [];
  }

  getPendingAreas(): any[] {
    if (this.activeMap && typeof this.activeMap.getPendingAreas === 'function') {
      return this.activeMap.getPendingAreas();
    }
    return [];
  }

  confirmPendingAreas(): void {
    if (this.activeMap && typeof this.activeMap.confirmPendingAreas === 'function') {
      this.activeMap.confirmPendingAreas();
    }
  }

  cancelBuildingFootprintDraft(): void {
    if (this.activeMap && typeof this.activeMap.cancelBuildingFootprintDraft === 'function') {
      this.activeMap.cancelBuildingFootprintDraft();
    }
  }

  undoPolygonEdit(): boolean {
    if (this.activeMap && typeof this.activeMap.undoPolygonEdit === 'function') {
      return this.activeMap.undoPolygonEdit();
    }
    return false;
  }

  get polygonCanUndo(): boolean {
    return !!(this.activeMap && this.activeMap.polygonCanUndo);
  }

  clearPendingAreas(): void {
    if (this.activeMap && typeof this.activeMap.clearPendingAreas === 'function') {
      this.activeMap.clearPendingAreas();
    }
  }

  clearDrawnAreas(): void {
    if (this.activeMap && typeof this.activeMap.clearDrawnAreas === 'function') {
      this.activeMap.clearDrawnAreas();
    }
  }

  setCustomMapAreas(areas: any): void {
    if (this.activeMap && typeof this.activeMap.setCustomMapAreas === 'function') {
      this.activeMap.setCustomMapAreas(areas);
      return;
    }
    this.pendingCustomMapAreas = { areas };
  }

  /** Site mapping: edit an existing saved building footprint on the offline map. */
  loadFootprintForEdit(params: {
    buildingId: string;
    footprint: [number, number][];
    anchor: { lat: number; lng: number; address?: string };
  }): void {
    if (this.activeMap && typeof (this.activeMap as any).loadFootprintForEdit === 'function') {
      (this.activeMap as any).loadFootprintForEdit(params);
    }
  }

  /** Site mapping (dialog map): seed new footprint at anchor after map ready. */
  startNewBuildingFootprintDraft(anchor: { lat: number; lng: number; address?: string }): void {
    if (this.activeMap && typeof (this.activeMap as any).startNewBuildingFootprintDraft === 'function') {
      (this.activeMap as any).startNewBuildingFootprintDraft(anchor);
    }
  }

  /** Fit camera to the current building footprint draft (after dialog open / resize). */
  fitBuildingFootprintDraftToView(): void {
    if (this.activeMap && typeof (this.activeMap as any).fitBuildingFootprintDraftToView === 'function') {
      (this.activeMap as any).fitBuildingFootprintDraftToView();
    }
  }

  /** Closed draft ring [lng,lat] or null. */
  getBuildingFootprintDraftRing(): [number, number][] | null {
    if (this.activeMap && typeof (this.activeMap as any).getBuildingFootprintDraftRing === 'function') {
      return (this.activeMap as any).getBuildingFootprintDraftRing();
    }
    return null;
  }

  addSector(id: string, options: {
    center: { lat: number, lng: number },
    radius: number,
    azimuth?: number,
    fov?: number,
    startAngle?: number,
    endAngle?: number,
    title?: string,
    editable?: boolean,
    showDots?: boolean,
    showTooltip?: boolean,
    clickable?: boolean,
    color?: string,
    showBorder?: boolean,
    fillOpacity?: number,
    lineOpacity?: number
  }): void {
    if (this.activeMap && typeof this.activeMap.addSector === 'function') {
      this.activeMap.addSector(id, options);
    }
  }

  addBox(id: string, options: { center: { lat: number, lng: number }, width: number, height: number, azimuth: number, title?: string, editable?: boolean }): void {
    if (this.activeMap && typeof this.activeMap.addBox === 'function') {
      this.activeMap.addBox(id, options);
    }
  }

  removeSector(id?: string): void {
    if (this.activeMap && typeof this.activeMap.removeSector === 'function') {
      this.activeMap.removeSector(id);
    }
  }

  removeBox(id?: string): void {
    if (this.activeMap && typeof this.activeMap.removeBox === 'function') {
      this.activeMap.removeBox(id);
    }
  }

  clearShapes(): void {
    if (this.activeMap) {
      this.removeSector();
      this.removeBox();
    }
  }

  /**
   * Fit the map view to a list of geolocations (center and zoom so all points are visible).
   * Affects the offline map when it is the active map.
   * @param geolocations Array of points with lat/lng or lat/lon
   * @param options Optional zoom (exact zoom for single point, or maxZoom when fitting bounds) and padding in pixels
   */
  fitToGeolocations(
    geolocations: Array<{ lat: number; lng?: number; lon?: number }>,
    options?: { zoom?: number; padding?: number }
  ): void {
    if (this.activeMap && typeof this.activeMap.fitToGeolocations === 'function') {
      this.activeMap.fitToGeolocations(geolocations, options);
    }
  }
  mapSelectTrigger(event: any, gotoLocation: boolean = false): void {
    if (this.activeMap && this.isClickable) {
      this.activeMap.mapClick(event, gotoLocation);
    }
  }

  private flushPendingMapOperations(): void {
    const map = this.activeMap;
    if (!map) {
      return;
    }

    if (this.pendingCustomMapAreas && typeof map.setCustomMapAreas === 'function') {
      const pending = this.pendingCustomMapAreas;
      this.pendingCustomMapAreas = null;
      map.setCustomMapAreas(pending.areas);
    }

    if (this.pendingAddMarkers && typeof map.addMarkers === 'function') {
      const pending = this.pendingAddMarkers;
      this.pendingAddMarkers = null;
      map.addMarkers(pending.markers, pending.options);
    }

    if (this.pendingMarkerUpdates.length && typeof map.updateMarkers === 'function') {
      const pendingUpdates = this.pendingMarkerUpdates.splice(0);
      pendingUpdates.forEach((pending) => {
        map.updateMarkers(pending.newMarkers, pending.calculateCenterZoom, pending.callback);
      });
    }

    if (this.pendingShowMarkers && typeof map.showMarkersIfReady === 'function') {
      const pending = this.pendingShowMarkers;
      this.pendingShowMarkers = null;
      map.showMarkersIfReady(pending.typeToHide, pending.calculateCenterZoom);
    }
  }
}
